import { useNavigate, useLocation } from 'react-router';
import { useState } from 'react';
import logoImage from '@/assets/RebookTblanco.png';
import { useAuth } from './auth-context';

interface TopNavProps {
  showBack?: boolean;
}

export function TopNav({ showBack = false }: TopNavProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const { lockApp, signOut, currentUser } = useAuth();
  
  // Determine if we should show back button based on route
  const isDetailScreen = location.pathname !== '/' && 
                         location.pathname !== '/appointments' && 
                         location.pathname !== '/insights' && 
                         location.pathname !== '/settings';
  
  const shouldShowBack = showBack || isDetailScreen;
  
  const user = {
    firstName: currentUser?.fullName.split(' ')[0] ?? '',
    fullName: currentUser?.fullName ?? '',
    role: currentUser?.roles?.[0] ?? '',
    initials: currentUser?.initials ?? '',
  };
  
  return (
    <nav 
      className="fixed top-0 left-0 right-0 flex items-center px-5"
      style={{
        backgroundColor: 'var(--dark-teal)',
        height: '64px',
        zIndex: 50
      }}
    >
      {/* Left Zone - Back Button */}
      <div className="flex-1 flex items-center">
        {shouldShowBack && (
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white text-sm hover:opacity-80 transition-opacity"
            style={{ minHeight: '44px' }}
          >
            <svg width="8" height="12" viewBox="0 0 8 12" fill="none" className="mt-0.5">
              <path d="M6 2L2 6L6 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Atrás</span>
          </button>
        )}
      </div>
      
      {/* Center Zone - Logo */}
      <div className="flex items-center justify-center">
        <img src={logoImage} alt="RebookT Logo" style={{ height: '32px', objectFit: 'contain' }} />
      </div>
      
      {/* Right Zone - User Dropdown */}
      <div className="flex-1 flex items-center justify-end relative">
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          style={{ minHeight: '44px' }}
        >
          {/* Avatar Circle */}
          <div 
            className="rounded-full flex items-center justify-center text-white font-medium"
            style={{
              width: '32px',
              height: '32px',
              backgroundColor: 'var(--terracotta)',
              fontSize: 'var(--text-xs)'
            }}
          >
            {user.initials}
          </div>
          
          {/* Full Name — hidden on mobile so the avatar stays balanced against the centered logo; shown from md+ */}
          <span className="text-white hidden md:inline" style={{ fontSize: 'var(--text-base)' }}>
            {user.fullName}
          </span>
          
          {/* Chevron */}
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none" className="mt-0.5">
            <path d="M1 1L5 5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        {/* Dropdown Panel */}
        {showDropdown && (
          <>
            {/* Backdrop to close dropdown */}
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setShowDropdown(false)}
            />
            
            {/* Dropdown Menu */}
            <div 
              className="absolute top-12 right-0 rounded-xl border z-50"
              style={{
                width: 'calc(100vw - 32px)',
                maxWidth: '240px',
                backgroundColor: 'var(--off-white)',
                borderColor: 'var(--border-color)',
                boxShadow: 'var(--shadow-lg)'
              }}
            >
              {/* User Header */}
              <div 
                className="border-b"
                style={{
                  padding: 'var(--space-2)',
                  borderColor: 'var(--border-color)'
                }}
              >
                <div 
                  className="mb-1" 
                  style={{ 
                    fontFamily: 'var(--font-heading)',
                    fontSize: '16px',
                    fontWeight: 'var(--font-weight-bold)',
                    color: 'var(--dark-teal)'
                  }}
                >
                  {user.fullName}
                </div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-fg)' }}>
                  {user.role}
                </div>
              </div>
              
              {/* Menu Items */}
              <div style={{ padding: '8px 0' }}>
                <button
                  onClick={() => {
                    setShowDropdown(false);
                    lockApp();
                  }}
                  className="w-full text-left hover:bg-[#FAF9F7] transition-colors"
                  style={{
                    padding: '12px 16px',
                    fontSize: 'var(--text-base)',
                    color: 'var(--dark-teal)',
                    minHeight: '44px'
                  }}
                >
                  Bloquear App
                </button>
                
                <button
                  onClick={() => {
                    setShowDropdown(false);
                    signOut();
                  }}
                  className="w-full text-left hover:bg-[#FAF9F7] transition-colors"
                  style={{
                    padding: '12px 16px',
                    fontSize: 'var(--text-base)',
                    color: 'var(--terracotta)',
                    minHeight: '44px'
                  }}
                >
                  Cerrar sesión
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </nav>
  );
}